import styled, { css } from 'styled-components';

import { Text } from '@/nova/components';
import { CFG } from '../../../../../../theme/configurator-tokens';
import { hiddenScrollbar, cardHover, spin } from '../../../../../../theme/configurator-keyframes';
import { ANIM } from '../../../../../../theme/tokens';
import { FINISH } from '../../finish.tokens';
import { SPEI } from '../../spei.tokens';

/* The method form deliberately reuses the Stripe checkout's shell (scrollable body + pinned
   total/CTA footer) so both Mexican rails feel like the same product as the international one;
   only the body content differs. Colours come from the same FINISH tokens for that reason. */

export const Root = styled.form`
  display: flex;
  height: 100%;
  flex-direction: column;
`;

export const Scroll = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
  padding: 0 12px 16px;
  ${hiddenScrollbar};
`;

export const SectionLabel = styled(Text)`
  font-size: 16px;
  line-height: 1;
  color: ${CFG.foreground};
`;

export const SectionLabelTop = styled(SectionLabel)`
  margin-top: 24px;
`;

/* ------------------------------- contact info ------------------------------ */

export const Field = styled.label`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldLabel = styled(Text)`
  font-size: 14px;
  line-height: 1.1;
  color: ${CFG.textMuted};
`;

export const Input = styled.input<{ $locked?: boolean }>`
  width: 100%;
  /* Explicit rather than inherited from Tailwind's preflight: the padding + 2px border must
     stay INSIDE the 100% width, or the field overflows its pane in any context that doesn't
     load the global reset (component tests, for one). */
  box-sizing: border-box;
  border-radius: 12px;
  border: 2px solid ${({ $locked }) => ($locked ? SPEI.copiedBorder : FINISH.inputBorder)};
  background: transparent;
  padding: 12px 14px;
  /* Room for the tick that sits over the trailing edge once locked. */
  padding-right: ${({ $locked }) => ($locked ? '38px' : '14px')};
  font-size: 16px;
  line-height: 24px;
  color: ${FINISH.inputText};

  &::placeholder {
    color: ${FINISH.inputPlaceholder};
  }
  &:focus {
    /* A locked field keeps its confirmed border even when clicked — it is read-only, so
       offering a focus ring would promise an edit that isn't going to happen. */
    border-color: ${({ $locked }) => ($locked ? SPEI.copiedBorder : FINISH.inputBorderFocus)};
    outline: none;
  }
  &[aria-invalid='true'] {
    border-color: ${FINISH.errorAlertBorder};
  }
`;

/** The name field and its Confirm/Edit button, side by side. */
export const FieldRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

/** Positioning context for the confirmed tick, and the part of the row that flexes. */
export const FieldControl = styled.div`
  position: relative;
  display: flex;
  min-width: 0;
  flex: 1;
`;

export const LockTick = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  display: flex;
  transform: translateY(-50%);
  /* Decorative: the field's own state is announced through its readOnly attribute. */
  pointer-events: none;
`;

/**
 * Confirm, and then Edit — and the emphasis HANDS OVER between them.
 *
 * While the name is unconfirmed, Confirm is the primary action, so it carries the brand fill.
 * That is not competing with the footer CTA: while the CTA is held it drops its brand fill
 * entirely, so there is only ever ONE orange control on screen and at that moment Confirm
 * genuinely IS the next thing to do.
 *
 * Once confirmed it drops to a ghost, the CTA lights up, and the only filled, coloured control
 * left on the screen is the one that takes the payment. Editing again reverses the handover.
 */
export const ConfirmButton = styled.button<{ $ghost?: boolean }>`
  flex-shrink: 0;
  cursor: pointer;
  align-self: stretch;
  border-radius: 12px;
  border: ${({ $ghost }) => ($ghost ? `1px solid ${FINISH.methodRowBorder}` : 'none')};
  background: ${({ $ghost }) => ($ghost ? 'transparent' : CFG.brand)};
  padding: 0 16px;
  transition:
    background ${ANIM.INTERACT_MS}ms ease,
    border-color ${ANIM.INTERACT_MS}ms ease,
    opacity ${ANIM.INTERACT_MS}ms ease;

  &:hover {
    background: ${({ $ghost }) => ($ghost ? CFG.wash : CFG.brand)};
    opacity: ${({ $ghost }) => ($ghost ? 1 : 0.9)};
  }
`;

export const ConfirmLabel = styled(Text)<{ $ghost?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  /* Primary takes the same ink as the footer CTA's label — the brand fill is a light surface. */
  color: ${({ $ghost }) => ($ghost ? CFG.textMuted : FINISH.payButtonText)};
`;

export const FieldError = styled(Text)`
  font-size: 13px;
  line-height: 1.2;
  color: ${FINISH.errorText};
`;

/* ------------------------------ method options ----------------------------- */

export const Methods = styled.div<{ $compact?: boolean }>`
  /* Without the section heading above it (compact), the group has to carry that spacing itself. */
  margin-top: ${({ $compact }) => ($compact ? '20px' : '12px')};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/**
 * One collapsed/expanded option.
 *
 * The border stays neutral in BOTH states on purpose. Selection is already carried by the filled
 * radio and by the option being the only one expanded; ringing the whole card in brand orange on
 * top of that read as an alert rather than as a choice, and fought with the orange CTA below it.
 */
export const Option = styled.div<{ $selected: boolean }>`
  border-radius: 12px;
  border: 1px solid ${FINISH.methodRowBorder};
  background: ${FINISH.methodRowBg};

  ${({ $selected }) =>
    !$selected &&
    css`
      ${cardHover}
    `}
`;

/**
 * The selectable half: a `<label>` around the radio, so the whole header row is a hit target and
 * not just the dot.
 *
 * Deliberately NOT wrapping the expanded body too. A `<label>` lends its entire text content to
 * the control inside it, so labelling the whole card made the radio announce the payer-name
 * field, the card form and the intro copy as its own name — and made every click inside the body
 * re-fire the radio.
 */
export const OptionHead = styled.label`
  display: flex;
  cursor: pointer;
  align-items: center;
  gap: 12px;
  padding: 14px;
`;

/** The real radio, kept in the DOM (and focusable) but painted by {@link RadioDot}. Clipped
 *  rather than `display: none` so assistive tech and keyboard users still reach it. */
export const RadioInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const RadioDot = styled.span<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  border: 2px solid ${({ $selected }) => ($selected ? CFG.brand : FINISH.inputBorder)};
  transition: border-color ${ANIM.INTERACT_MS}ms ease;

  &::after {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 9999px;
    background: ${CFG.brand};
    opacity: ${({ $selected }) => ($selected ? 1 : 0)};
    transition: opacity ${ANIM.INTERACT_MS}ms ease;
  }

  ${RadioInput}:focus-visible + & {
    outline: 2px solid ${CFG.brand};
    outline-offset: 2px;
  }
`;

export const OptionLabels = styled.span`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 2px;
`;

export const OptionTitle = styled(Text)`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.25;
  color: ${CFG.foreground};
`;

export const OptionSub = styled(Text)`
  font-size: 13px;
  line-height: 1.3;
  color: ${CFG.textMuted};
`;

/** Trailing mark (the SPEI wordmark, the card glyph). */
export const OptionMark = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
`;

/** The expanded half of an option. Rendered only when selected — not merely zero-height — so a
 *  collapsed rail's inputs are out of the tab order and out of the accessibility tree. */
export const OptionBody = styled.div`
  border-top: 1px solid ${FINISH.methodRowBorder};
  padding: 14px;

  /* Fields in here sit on the option's RAISED surface, not on the pane, so the transparent fill
     the email field uses would read as part of the card. Scoped from the body rather than
     threaded through as a prop, so the SPEI payer-name field and anything added later inherit it
     without either side having to remember. Stripe's own inputs get the matching literal through
     the Elements appearance — its iframe can't read this variable. */
  ${Input}:not([readonly]) {
    background: ${SPEI.fieldBg};
  }
`;

/** The expanded transfer rail's explanation. A paragraph rather than a single `<Text>`, because
 *  its opening sentence is dropped on mobile (see `IntroSentence`). */
export const OptionIntro = styled.p`
  font-size: 13px;
  line-height: 1.45;
  color: ${CFG.textMuted};
`;

/** One sentence of {@link OptionIntro}, so the copy can be composed rather than re-authored per
 *  breakpoint — each sentence stays its own translatable string. */
export const IntroSentence = styled(Text)`
  display: inline;
`;

/* --------------------------- compact (mobile) tabs -------------------------- */

/**
 * The mobile layout of the same choice: a segmented control instead of stacked cards.
 *
 * Two full-width cards plus the selected one's body pushed the second rail off the bottom of the
 * sheet — a payment method the buyer had to scroll to discover. Side by side, both are visible
 * before anything is filled in.
 *
 * The RADIO semantics are unchanged (this is still one choice out of two, and the group is still
 * a `radiogroup` of real radios) — only the arrangement differs. Tab semantics would have implied
 * tab panels and arrow-key navigation, which is not what a payment method is.
 */
export const TabStrip = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 4px;
  border-radius: 14px;
  border: 1px solid ${FINISH.methodRowBorder};
  background: ${CFG.wash};
  padding: 4px;
`;

/** One segment. Selected is a RAISED surface rather than a brand tint — the same reason the
 *  desktop cards dropped their orange ring: only the CTA gets to be orange. */
export const Tab = styled.label<{ $selected: boolean }>`
  display: flex;
  min-width: 0;
  flex: 1;
  cursor: pointer;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 10px;
  background: ${({ $selected }) => ($selected ? FINISH.methodRowBg : 'transparent')};
  padding: 10px 8px;
  transition: background ${ANIM.INTERACT_MS}ms ease;

  &:focus-within {
    outline: 2px solid ${CFG.brand};
    outline-offset: -2px;
  }
`;

/** Fixed height so the two segments align whatever their marks' aspect ratios are. */
export const TabMark = styled.span`
  display: flex;
  height: 22px;
  align-items: center;
`;

export const TabLabel = styled(Text)<{ $selected: boolean }>`
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  color: ${({ $selected }) => ($selected ? CFG.foreground : CFG.textMuted)};
  transition: color ${ANIM.INTERACT_MS}ms ease;
`;

/** The selected segment's content, below the strip. */
export const TabPanel = styled.div`
  margin-top: 10px;
  border-radius: 12px;
  border: 1px solid ${FINISH.methodRowBorder};
  background: ${FINISH.methodRowBg};
  padding: 14px;

  /* Same rule as OptionBody: a field on this raised surface needs its own fill to read as a
     field, and a locked one goes back to transparent. */
  ${Input}:not([readonly]) {
    background: ${SPEI.fieldBg};
  }
`;

/** The Powered-by-Stripe badge's home in the compact layout, where the segment has room for the
 *  card marks but not for a lockup underneath them. */
export const TabPanelBadge = styled.div`
  margin-bottom: 12px;
`;

/* ------------------------------- card status ------------------------------- */

export const CardStatusBox = styled.div`
  display: flex;
  min-height: 96px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
`;

export const Spinner = styled.img<{ $onLight: boolean }>`
  width: 28px;
  height: 28px;
  filter: ${({ $onLight }) => ($onLight ? 'invert(1)' : 'none')};
`;

export const CardStatusText = styled(Text)`
  font-size: 13px;
  line-height: 1.35;
  color: ${CFG.textMuted};
`;

export const RetryButton = styled.button`
  display: inline-flex;
  height: 36px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: 1px solid ${FINISH.methodRowBorder};
  background: transparent;
  padding: 0 16px;
`;

export const RetryLabel = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  color: ${CFG.foreground};
`;

/* -------------------------------- error alert ------------------------------ */

export const ErrorAlert = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-radius: 12px;
  border: 1px solid ${FINISH.errorAlertBorder};
  background: ${FINISH.errorAlertBg};
  padding: 12px 14px;
`;

export const ErrorAlertIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  align-items: center;
  justify-content: center;
`;

export const ErrorAlertText = styled(Text)`
  font-size: 14px;
  line-height: 1.35;
  color: ${FINISH.errorText};
`;

/* ------------------------------ total + submit ----------------------------- */

export const Footer = styled.div`
  flex-shrink: 0;
  padding: 8px 12px 16px;
`;

export const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${FINISH.paneDivider};
  padding: 12px;
`;

export const TotalLabels = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TotalLabel = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
  color: ${CFG.base50};
`;

export const TotalSub = styled(Text)`
  font-size: 14px;
  line-height: 1.1;
  color: ${FINISH.termsText};
`;

export const TotalAmount = styled(Text)`
  white-space: nowrap;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  color: ${CFG.foreground};
`;

export const SubmitButton = styled.button`
  margin-top: 12px;
  display: flex;
  height: 48px;
  width: 100%;
  cursor: pointer;
  ${cardHover}
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 9999px;
  border: none;
  background: ${CFG.brand};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${FINISH.payButtonText};
  transition: opacity ${ANIM.INTERACT_MS}ms ease;

  /* A held CTA drops the brand fill entirely rather than dimming it. A faded orange still reads
     as "the button", which is exactly wrong while the real next action is the Confirm above it. */
  &:disabled {
    cursor: default;
    background: ${CFG.surfaceRaised};
    color: ${CFG.textMuted};
  }
  &:disabled:hover::before,
  &:disabled:hover::after,
  &:disabled:focus-visible::before,
  &:disabled:focus-visible::after {
    opacity: 0;
  }
`;

export const SubmitLabel = styled(Text)`
  display: inline;
`;

/**
 * Activity ring inside the CTA, for the waits the buyer cannot see the cause of: the invoice call
 * that fetches the bank coordinates, and Stripe becoming chargeable.
 *
 * Drawn in `currentColor` rather than a token of its own, deliberately — the button is brand
 * orange with black ink when live and a neutral surface with muted ink while held, and the ring
 * has to read on both without knowing which one it is on.
 *
 * Kept subtle on purpose (2px, 60% opacity): it is a reassurance that something is happening, not
 * an alarm. Honours `prefers-reduced-motion` like the rest of the area's animations.
 */
export const CtaSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 9999px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  opacity: 0.6;
  animation: ${spin} ${ANIM.SPIN_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
